/**
 * Generic Promise wrapper around a Web Worker round-trip.
 *
 * SRP: this module ONLY manages worker lifecycle (spawn → postMessage →
 * await one response → terminate). It knows nothing about the message
 * payload semantics — those are typed by the caller.
 *
 * Why it exists:
 *  - Workers communicate via events; consumers usually want a Promise.
 *  - Each call gets its own worker instance and terminates it on completion,
 *    so callers don't leak workers if they spawn many small jobs.
 *
 * Usage:
 *   type Req  = { type: "parse"; buffer: ArrayBuffer }
 *   type Resp =
 *     | { type: "parse-success"; rows: unknown[] }
 *     | { type: "error"; message: string }
 *
 *   const result = await sendToWorker<Resp, Extract<Resp, { type: "parse-success" }>>(
 *     () => new Worker(new URL("./my.worker.ts", import.meta.url), { type: "module" }),
 *     { type: "parse", buffer },
 *     "parse-success",
 *     [buffer] // transferable list — zero-copy for ArrayBuffers
 *   )
 */

/**
 * The minimum shape any worker response must follow: a discriminant `type`
 * field. "error" is reserved for failure responses.
 */
export type WorkerResponseBase = { type: string }

export type WorkerErrorResponse = { type: "error"; message: string }

/**
 * Send a single request to a fresh worker and await one matching response.
 *
 * Resolves with the response whose `type` equals `successType`.
 * Rejects on:
 *   - A response with `type: "error"` (message is propagated)
 *   - Any other response type (treated as a contract violation)
 *   - A worker `onerror` event
 *
 * @param spawn        Factory that returns a new Worker. Receives no args.
 * @param request      The message payload to post.
 * @param successType  The discriminant value that indicates success.
 * @param transfer     Optional Transferable[] for zero-copy (e.g. ArrayBuffers).
 */
export const sendToWorker = <
  TResponse extends WorkerResponseBase | WorkerErrorResponse,
  TSuccess extends TResponse
>(
  spawn: () => Worker,
  request: unknown,
  successType: TSuccess["type"],
  transfer: Transferable[] = []
): Promise<TSuccess> =>
  new Promise((resolve, reject) => {
    const worker = spawn()

    worker.onmessage = (event: MessageEvent<TResponse>) => {
      const data = event.data
      worker.terminate()

      if (data.type === successType) {
        resolve(data as TSuccess)
        return
      }

      if (data.type === "error") {
        reject(new Error((data as WorkerErrorResponse).message))
        return
      }

      reject(new Error(`Unexpected worker response: ${String(data.type)}`))
    }

    worker.onerror = (err) => {
      worker.terminate()
      reject(err)
    }

    worker.postMessage(request, transfer)
  })
